import {Component, Mixins} from 'vue-property-decorator';
import {
    Category,
    FieldName,
    JsonData,
    Product,
    ProductServiceItem,
    ProductType,
    SelectedOptions,
    SelectedServiceProduct,
    Service,
    ServiceType
} from '@/products-and-services/ProductsAndServicesModels';
import {CrossSaleAudienceType, CrossSalePageRoutes} from '@/products-and-services/CrossSaleModels';
import SearchMixin from './SearchMixin';
import CartErrorsMixin from './CartErrorsMixin';

@Component
export default class ProductMixin  extends Mixins(SearchMixin,CartErrorsMixin) {
    protected isInCart(item: SelectedServiceProduct) {
        return this.$store.getters.isInCart(item);
    }

    async addToCart({path, ...item}: SelectedServiceProduct) {
        try {
            if (!this.$store.getters.getLocation) {
                //changes modal to ask for address
                this.showAddressModal = true;
                this.$store.commit('storeItem', item);
            } else {
                this.$store.commit('setCatItemBeingProcessed', item);
                await this.$store.dispatch('updateOrderDetails', {mutation: 'addToCart', item});
                if(!item.noRefetch) {
                    await this.fetchCrossSales();
                }
                if (path) {
                    this.$router.push({path});
                }
            }
        } catch (error) {
            this.$notifyError(error);
        }
        finally {
            this.$store.commit('setCatItemBeingProcessed', {});
        }
    }

    async removeFromCart(item: SelectedServiceProduct) {
        try {
            this.$store.commit('setCatItemBeingProcessed', item);
            await this.$store.dispatch('updateOrderDetails', {mutation: 'removeFromCart', item});
            if(!item.noRefetch) {
                await this.fetchCrossSales();
            }
        } catch (error) {
            this.$notifyError(error);
        }
        finally {
            this.$store.commit('setCatItemBeingProcessed', {});
        }
    }

    async fetchCrossSales() {
        await this.$store.dispatch('loadCrossSales', {pageId: CrossSalePageRoutes['productsAndServices'], audience: this.crossSaleAudienceType});
    }

    get crossSaleAudienceType() {
        if (window.location.pathname.includes('residential')) {
            return CrossSaleAudienceType.RESIDENCE;
        }
        return CrossSaleAudienceType.BUSINESS;
    }

    get selectedOptions(): SelectedOptions {
        return this.$store.getters.getShoppingCartSelection();
    }

    isCatItemBeingProcessed(catId: string): boolean {
        return  this.$store.state.catItemBeingProcessed[catId];
    }

    getCatSelectedProduct({serviceId, catId: serviceCatId}: Service, {productId, catId: productCatId}: Product) {
        if (this.selectedOptions && this.selectedOptions.services) {
            if (productCatId && productId) {
                const service = this.selectedOptions.services.find(serv => serv.catId === serviceCatId && serv.serviceId.toString() === serviceId.toString()) as Service;
                if (service && service.products) {
                    const product = service.products.find(prd => prd.catId === productCatId && prd.productId.toString() === productId.toString()) as Product;
                    if (product) {
                        return {isSelected: true, value: product.value};
                    }
                }
            }
        }
        return {isSelected: false};
    }

    getAvailableServices() {
        const serviceTypes = this.$store.getters.getAvailableServices() as ServiceType[];
        if (serviceTypes) {
            return serviceTypes.reduce((acc: Service[], {id: serviceTypeId, services}: ServiceType) => {
                if (services && services.length) {
                    const servicesWithTypeId = services.map(ser => ({...ser, serviceTypeId}));
                    return [...acc, ...servicesWithTypeId];
                }
                return acc;
            }, [] as Service[]);
        }
    }

    getAvailableProductsByType(selectedServices: Service[]): Product[] | undefined {
        const availServices = this.getAvailableServices() || [];

        // get filtered services by type bateryBackup, checkboxes and phoneOptions type
        return selectedServices.reduce((acc: any, selectedService: Service) => {
            const availService = availServices.find(s =>
                s.serviceTypeId
                && selectedService.serviceTypeId
                && s.serviceTypeId.toString() === selectedService.serviceTypeId.toString()
                && s.serviceId.toString() === selectedService.serviceId.toString()
                && s.catId === selectedService.catId);
            if (availService && availService.categories && availService.categories.length) {
                const availProducts = availService.categories.reduce((cats: Product[], cat: Category) => ([...cats, ...cat.products]), [] as Product[]);
                return [...acc, ...availProducts.map(ap => ({
                    ...ap,
                    serviceTypeId: selectedService && selectedService.serviceTypeId ? selectedService.serviceTypeId.toString() : '',
                    serviceId: selectedService.serviceId.toString(),
                    serviceCatId: selectedService.catId}))];
            }
            return acc;
        }, [] as Product[]);
    }

    shoppingCartItemsSetAvailProducts([product]: any, availProducts: Product[], productTypes: ProductType[]): Product[] {
        if (product) {
            const typeProducts =  product.products;
            const filteredProducts = availProducts
            .filter(sap => productTypes.some(typeId => typeId === sap.typeId))
            .map(p => {
                const prod = typeProducts.find((prd: any) => prd.catId === p.catId && prd.productId === p.productId) as Product;
                if (prod) {
                    return {...p, ...prod, isSelected: true};
                }
                return p;
            });
            return [{...product, isFullCart: true, products: filteredProducts}];
        }
        return [];
    }

    getManagedServices(services: Service[]): Service[] {
        if (!(services && services.length)) {
            return [];
        }

        const availProducts = this.getAvailableProductsByType(services) || [];

        return services.reduce((acc: any, service: Service) => {
            const products = service.products as Product[];
            const serviceAvailProducts = availProducts.filter(ap =>
                ap.serviceId === service.serviceId
                && ap.serviceTypeId === service.serviceTypeId
                && ap.serviceCatId === service.catId);

            const checkboxes = this.shoppingCartItemsSetAvailProducts(
                this.getProductsByType(products, [ProductType.CHECKBOX, ProductType.CHECKBOX_WITH_EMAIL], service, "checkboxes"),
                serviceAvailProducts,
                [ProductType.CHECKBOX, ProductType.CHECKBOX_WITH_EMAIL]);
            const batteryBackup = this.shoppingCartItemsSetAvailProducts(
                this.getProductsByType(products, ProductType.BATTERY_BACKUP, service, "battery-backup"),
                serviceAvailProducts,
                [ProductType.BATTERY_BACKUP]);
            const phoneNumberPublishing = this.shoppingCartItemsSetAvailProducts(
                this.getProductsByType(products, ProductType.PHONE_NUMBER_PUBLISHING, service, "phone-number-publishing"),
                serviceAvailProducts,
                [ProductType.PHONE_NUMBER_PUBLISHING]);
            const cloudBackUpEmail = this.shoppingCartItemsSetAvailProducts(
                this.getProductsByType(products, ProductType.CLOUD_BACKUP_EMAIL, service, "cloud-backup"),
                serviceAvailProducts,
                [ProductType.CLOUD_BACKUP_EMAIL]);
            const cloudBackUpProducts = products.filter(product => product.typeId === ProductType.CLOUD_BACKUP_DISCOUNT || product.typeId === ProductType.CLOUD_BACKUP_AMOUNT);
            const cloudBackUp = cloudBackUpEmail;

            const streamingDevices = this.shoppingCartItemsSetAvailProducts(
                this.getProductsByType(products, ProductType.STREAMING_DEVICES, service, "streaming-devices"),
                serviceAvailProducts,
                [ProductType.STREAMING_DEVICES]);

            if (cloudBackUp[0]) {
                cloudBackUp[0].products.push(...cloudBackUpProducts);
            }

            const others = products.filter(({typeId}: Product) =>
            ![ProductType.CHECKBOX, ProductType.CLOUD_BACKUP_EMAIL, ProductType.BATTERY_BACKUP, ProductType.PHONE_NUMBER_PUBLISHING, ProductType.CLOUD_BACKUP_AMOUNT, ProductType.CLOUD_BACKUP_DISCOUNT, ProductType.CLOUD_BACKUP_EMAIL, ProductType.STREAMING_DEVICES].some(type => type === typeId))
                .map((prod, index) => this.getCustomInputProductsByType(prod, service, index, true));

            const newProducts = [
                ...others.sort(({productId: a}: Product, {productId: b}: Product) => Number(a) - Number(b)),
                ...batteryBackup,
                ...phoneNumberPublishing,
                ...checkboxes,
                ...cloudBackUp,
                ...streamingDevices
            ];

            if (newProducts.length) {
                return [...acc, {...service, products: newProducts}];
            }
            return [...acc, {...service, products}];
        }, [] as Service[]).sort(({serviceId: a}: Service, {serviceId: b}: Service) => Number(a) - Number(b));
    }

    getManagedService(service?: Service): Service {
        if (service) {
            if (service.categories) {
                // group custom types.
                const categories = (service.categories as any).reduce((acc: Category[], {products: prods, ...category}: Category) => {
                    // add cart selection to product value
                    const products = prods.map(pr => ({...pr, ...this.getCatSelectedProduct(service, pr)}));

                    const checkboxes = this.getProductsByType(products, [ProductType.CHECKBOX, ProductType.CHECKBOX_WITH_EMAIL], service, "checkboxes");
                    const batteryBackup = this.getProductsByType(products, ProductType.BATTERY_BACKUP, service, "battery-backup");
                    const phoneNumberPublishing = this.getProductsByType(products, ProductType.PHONE_NUMBER_PUBLISHING, service, "phone-number-publishing");
                    const cloudBackUpEmail = this.getProductsByType(products, ProductType.CLOUD_BACKUP_EMAIL, service, "cloud-backup")
                    const cloudBackUpProducts = products.filter(product => product.typeId === ProductType.CLOUD_BACKUP_DISCOUNT || product.typeId === ProductType.CLOUD_BACKUP_AMOUNT);
                    const cloudBackUp = cloudBackUpEmail;
                    const staticIpAddress = this.getProductsByType(products, ProductType.STATIC_IP_ADDRESSES, service, "StaticIPAddresses")
                    const businessTv = this.getProductsByType(products, ProductType.BUSINESS_TV, service, "business-tv")
                    const streamingDevices = this.getProductsByType(products, ProductType.STREAMING_DEVICES, service, 'streaming-devices');
                    if (cloudBackUp[0]) {
                        cloudBackUp[0].products.push(...cloudBackUpProducts);
                    }
                    const others = products.filter(({typeId}: Product) =>
                        ![ProductType.CHECKBOX,
                        ProductType.CHECKBOX_WITH_EMAIL,
                        ProductType.BATTERY_BACKUP,
                        ProductType.PHONE_NUMBER_PUBLISHING,
                        ProductType.CLOUD_BACKUP_AMOUNT,
                        ProductType.CLOUD_BACKUP_DISCOUNT,
                        ProductType.CLOUD_BACKUP_EMAIL,
                        ProductType.STATIC_IP_ADDRESSES,
                        ProductType.BUSINESS_TV,
                        ProductType.STREAMING_DEVICES]
                    .some(type => type === typeId))
                    .map((prod, index) => this.getCustomInputProductsByType(prod, service, index));
                    const newProducts = [...others, ...batteryBackup, ...phoneNumberPublishing, ...checkboxes, ...cloudBackUp, ...staticIpAddress, ...businessTv, ...streamingDevices];

                    if (newProducts.length) {
                        return [...acc, {...category, products: newProducts}];
                    }
                    return [...acc, {...category, products}];
                }, [] as Category[]);
                return {...service, categories};
            }
            return service;
        }
        return {} as Service;
    }

    getCustomInputProductsByType(product: Product, service: Service, index: number, isFullCart = false) {
        const {serviceTypeId, serviceId, catId: serviceCatId = ''} = service;
        const item = {...product, productCatId: product.catId, index, serviceTypeId, serviceId, serviceCatId};

        switch (product.typeId) {
            case ProductType.ADDITIONAL_DVR:
                return {component: 'additional-dvr', ...product, isFullCart, item};
            case ProductType.AVAST_ANTIVIRUS_EMAIL:
                return {component: 'avast-aniti-virus-email', ...product, isFullCart, item};
            case ProductType.EMAIL_ADDRESSES:
                return {component: 'custom-email-addresses', ...product, isFullCart, item};
            case ProductType.NUMBER_OF_TV:
                return {component: 'number-of-tv', ...product, isFullCart, item};
            case ProductType.PHONE_NUMBER:
                return {component: 'phone-options', ...product, isFullCart, item};
            default:
                return {component: 'content-card', ...product, isFullCart, item};
        }
    }


    getProductsByType(allProducts: Product[], productType: ProductType | ProductType[], service: Service, component: string) {
        const productTypes = Array.isArray(productType) ? productType : [productType];
        const {serviceTypeId, serviceId, catId: serviceCatId = ''} = service;
        const serviceInfo = {serviceTypeId, serviceId, serviceCatId};
        const products = allProducts.filter(({typeId}: Product) => productTypes.some(typeToCheck => typeId === typeToCheck));
        if (products.length) {
            return [{ component, products, typeId: productTypes[0], ...serviceInfo }];
        }
        return [];
    }

    getProductServiceItem(product: Product, service: Service, index: number, productCatId: string): ProductServiceItem {
        const {serviceTypeId, serviceId, catId: serviceCatId = ''} = service;
        return {...product, productCatId, serviceTypeId, serviceId, serviceCatId, index};
    }

    getSelectedProduct({productId, catId: productCatId}: Product, {serviceTypeId, serviceId, catId: serviceCatId}: Service): SelectedServiceProduct {
        return {serviceId, serviceTypeId, serviceCatId, productId, productCatId};
    }

    jsonParseFromValue(value: string | undefined): JsonData[] {
        if (value) {
            return JSON.parse(value);
        }
        return [];
    }

    // noinspection JSUnusedGlobalSymbols It certainly is used
    jsonStringifyValue(value: JsonData[]): string {
        return JSON.stringify(value);
    }

    getProductValue(value: string) {
        const [val,val2] = this.jsonParseFromValue(value);
        if(val && val2){
            return val.value + ' - ' + val2.value + ' address';
        }
        if (val) {
            switch (val.field) {
                case FieldName.NUMBER_OF_STATIC_IP_ADDRESS:
                case FieldName.ADDITIONAL_DVR:
                case FieldName.NUMBER_OF_TV:
                    return `Quantity x${val.value}`;
                case FieldName.EMAIL_ADDRESSES:
                    return '';
                default:
                    return val.value;
            }
        }
        return '';
    }

    get isResidential(): boolean {
        return window.location.pathname.includes('residential');
    }
}
